import type { HexColor } from '@getaccept/fried-tofu';
import { hexToHsl, hexToRgb, hslToHex } from '../../helpers';

export default class ColorUtilsHelper {
  static getLuminance(r: number, g: number, b: number) {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  static getContrastRatio(l1: number, l2: number) {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  static getAccessibleTextColor(backgroundColor: string) {
    const [r, g, b] = hexToRgb(backgroundColor);
    const bgLuminance = this.getLuminance(r, g, b);

    const whiteContrast = this.getContrastRatio(1, bgLuminance);
    const blackContrast = this.getContrastRatio(0, bgLuminance);

    // WCAG Level AA
    const targetContrast = 4.5;

    if (whiteContrast >= targetContrast && whiteContrast > blackContrast) {
      return '#FFFFFF';
    } else if (blackContrast >= targetContrast) {
      return '#2C3B4E';
    }
    return bgLuminance > 0.5 ? '#2C3B4E' : '#FFFFFF';
  }

  static calculateSecondaryColor(colorOne: HexColor): HexColor {
    const colorOneHSL = hexToHsl(colorOne);

    if (colorOneHSL.l > 80) {
      return hslToHex(colorOneHSL.h, colorOneHSL.s, Math.max(colorOneHSL.l - 20, 0));
    }

    if (colorOneHSL.l < 20) {
      return hslToHex(colorOneHSL.h, colorOneHSL.s, Math.min(colorOneHSL.l + 20, 100));
    }

    const colorTwoL = Math.min(Math.max(colorOneHSL.l + 10, 0), 100);
    return hslToHex(colorOneHSL.h, colorOneHSL.s, colorTwoL);
  }

  static lightenColor(color: HexColor, percentage: number): HexColor {
    if (percentage < 0 || percentage > 100) {
      throw new Error('Percentage must be between 0 and 100');
    }

    const colorHSL = hexToHsl(color);

    const remainingLightness = 100 - colorHSL.l;
    const increasedLightness = (remainingLightness * percentage) / 100;
    const newLightness = Math.min(colorHSL.l + increasedLightness, 100);

    return hslToHex(colorHSL.h, colorHSL.s, newLightness);
  }

  static calculateHoverColor(baseColor: HexColor): HexColor {
    const baseHSL = hexToHsl(baseColor);

    const newLightness = baseHSL.l + 10 <= 100 ? baseHSL.l + 10 : baseHSL.l - 10;

    return hslToHex(baseHSL.h, baseHSL.s, newLightness);
  }

  static calculateOverlayColor(lightness: number) {
    const color = lightness < 0 ? 'rgba(0, 0, 0,' : 'rgba(255, 255, 255,';
    const opacity = Math.abs(lightness) / 50;
    return `${color}${opacity})`;
  }

  static calculateLinearGradient(primaryColor: HexColor, secondaryColor: HexColor) {
    return `linear-gradient(to top right, ${primaryColor}, ${secondaryColor})`;
  }

  static calculateRadialGradient(primaryColor: HexColor, secondaryColor: HexColor) {
    return `radial-gradient(${primaryColor}, ${secondaryColor})`;
  }

  static calculateConicGradient(primaryColor: HexColor, secondaryColor: HexColor) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const angleToTopRight = Math.atan2(-viewportHeight, viewportWidth) * (180 / Math.PI);
    const fromAngle = 90 + angleToTopRight;

    return `conic-gradient(from ${fromAngle}deg, ${primaryColor}, ${secondaryColor})`;
  }

  static calculateSolidLinearGradientWithOverlay(primaryColor: HexColor, overlayColor: string) {
    return `linear-gradient(${overlayColor}, ${overlayColor}), ${primaryColor}`;
  }

  static calculateLinearGradientWithOverlay(linearGradient: string, overlayColor: string) {
    return `linear-gradient(${overlayColor}, ${overlayColor}), ${linearGradient}`;
  }

  static calculateRadialGradientWithOverlay(radialGradient: string, overlayColor: string) {
    return `linear-gradient(${overlayColor}, ${overlayColor}), ${radialGradient}`;
  }

  static calculateConicGradientWithOverlay(conicGradient: string, overlayColor: string) {
    return `linear-gradient(${overlayColor}, ${overlayColor}), ${conicGradient}`;
  }
}
